import { enhancedApi } from '@store/generated/encounters';
import type { RootState } from '@store/index';

import { ENCOUNTER_CACHE_TTL, ENCOUNTER_TAG_TYPES } from '../constants';

import { selectActiveWorkflowEncounterId } from './selectors';
import { clearActiveWorkflowEncounter, setActiveWorkflowEncounter } from './workflowSlice';

/**
 * Encounters API - extends the generated OpenAPI client
 *
 * This ensures we're using generated types and endpoints
 * while maintaining feature-specific customization with:
 * - Cache tags for automatic invalidation
 * - Optimistic updates for better UX
 * - Integration with workflow state
 * - Custom V1 endpoints not yet in OpenAPI spec
 * - Bidirectional cache invalidation with appointments feature
 */
export const encountersApi = enhancedApi
  .enhanceEndpoints({
    addTagTypes: [
      ...ENCOUNTER_TAG_TYPES,
      // Cross-feature tags for bidirectional cache invalidation
      // These allow encounter mutations to invalidate appointment caches
      'NextAppointment',
      'AppointmentsList',
    ],
    endpoints: {
      // Enhance: Get single encounter
      getV2ProviderEncountersByEncounterId: {
        providesTags: (_result: any, _error: any, { encounterId }: any) => [
          { type: 'Encounter' as const, id: encounterId },
        ],
        keepUnusedDataFor: ENCOUNTER_CACHE_TTL,
      },

      // Enhance: List encounters
      getV2ProviderEncounters: {
        providesTags: (result: any) =>
          result
            ? [
                ...result.items.map(({ encounter_id }: any) => ({
                  type: 'Encounter' as const,
                  id: encounter_id,
                })),
                { type: 'EncounterList' as const, id: 'LIST' },
              ]
            : [{ type: 'EncounterList', id: 'LIST' }],
        keepUnusedDataFor: ENCOUNTER_CACHE_TTL,
      },

      // Enhance: Create encounter
      postV2ProviderEncounters: {
        invalidatesTags: [
          { type: 'EncounterList', id: 'LIST' },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is created, appointment caches must be cleared
          // to reflect that the appointment now has an associated encounter
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
        // Phase 2: Automatically set as active workflow after creation
        onQueryStarted: async (_arg: any, { dispatch, queryFulfilled }: any) => {
          try {
            const { data: newEncounter } = await queryFulfilled;
            dispatch(
              setActiveWorkflowEncounter({
                encounterId: newEncounter.encounter_id,
                reason: 'created',
              }),
            );
          } catch (error) {
            // Error already handled by mutation state
          }
        },
      },

      // Enhance: Update encounter
      patchV2ProviderEncountersByEncounterId: {
        invalidatesTags: (_result: any, _error: any, { encounterId }: any) => [
          { type: 'Encounter', id: encounterId },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is updated (patient changed, template changed, etc),
          // appointment caches must be cleared to reflect the updated state
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
        // Optimistic update for better UX
        onQueryStarted: async (
          { encounterId, encounterUpdate }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterId',
              { encounterId },
              (draft: any) => {
                Object.assign(draft, encounterUpdate);
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },

      // Enhance: Delete encounter
      deleteV2ProviderEncountersByEncounterId: {
        invalidatesTags: [
          // NOTE: Do NOT invalidate specific encounter tag on delete
          // Invalidating { type: 'Encounter', id: encounterId } causes RTK Query to refetch
          // the deleted encounter (and its documents), resulting in 404 errors.
          // Instead, only invalidate lists so they update to reflect the deletion.
          { type: 'EncounterList', id: 'LIST' },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is deleted, appointment caches must be cleared
          // to reflect that the appointment is now available again (no encounter)
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
        // Phase 2: Clear workflow state if deleting active encounter
        onQueryStarted: async (
          { encounterId }: any,
          { dispatch, getState, queryFulfilled }: any,
        ) => {
          try {
            await queryFulfilled;
            // Check if deleting active workflow encounter
            const state = getState() as RootState;
            const activeId = selectActiveWorkflowEncounterId(state);
            if (activeId === encounterId) {
              dispatch(clearActiveWorkflowEncounter({ reason: 'encounter-deleted' }));
            }
          } catch (error) {
            // Error handled by mutation state
          }
        },
      },

      // Enhance: Get single document
      getV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
        providesTags: (_result: any, _error: any, { encounterId, documentId }: any) => [
          { type: 'EncounterDocument' as const, id: documentId },
          // { type: 'Encounter' as const, id: encounterId },
        ],
        keepUnusedDataFor: ENCOUNTER_CACHE_TTL,
      },

      // Enhance: Create document
      postV2ProviderEncountersByEncounterIdDocuments: {
        invalidatesTags: [
          // NOTE: Don't invalidate Encounter here - calling code (EncounterDocuments,
          // EncounterRecordingWidget) always calls saveNow() immediately after which
          // invalidates and refetches the encounter. This prevents double refetches.
        ],
        // Optimistic update for better UX - immediately show new document in encounter.inputs
        onQueryStarted: async (
          { encounterId, createDocumentRequest }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          // Use transcription_session_id as a deterministic temp ID to prevent duplicates
          // when onQueryStarted is triggered multiple times (e.g., via redux-state-sync)
          const tempId = createDocumentRequest.transcription_session_id
            ? `temp-${createDocumentRequest.transcription_session_id}`
            : `temp-${Date.now()}`;

          // Optimistically add to encounter.inputs
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterId',
              { encounterId },
              (draft: any) => {
                if (!draft.inputs) {
                  draft.inputs = [];
                }

                // Check if temp entry with this ID already exists (prevents duplicates from double dispatch)
                const existingTempIndex = draft.inputs.findIndex(
                  (input: any) => input.encounter_document_id === tempId,
                );
                if (existingTempIndex !== -1) {
                  return;
                }

                // Add temporary document reference that will be replaced by server response
                draft.inputs.push({
                  encounter_document_id: tempId,
                  type: createDocumentRequest.type,
                  title: createDocumentRequest.title || null,
                  created_at: new Date().toISOString(),
                  status: 'pending' as const,
                });
              },
            ),
          );

          try {
            const { data: createdDocument } = await queryFulfilled;
            // Update the temporary entry with the real document ID from server
            dispatch(
              encountersApi.util.updateQueryData(
                'getV2ProviderEncountersByEncounterId',
                { encounterId },
                (draft: any) => {
                  if (draft.inputs) {
                    // Find the specific temp entry we created
                    const tempIndex = draft.inputs.findIndex(
                      (input: any) => input.encounter_document_id === tempId,
                    );
                    if (tempIndex !== -1 && createdDocument) {
                      // Check if real document already exists (another dispatch may have replaced it)
                      const existingRealIndex = draft.inputs.findIndex(
                        (input: any) =>
                          input.encounter_document_id === createdDocument.encounter_document_id,
                      );
                      if (existingRealIndex !== -1 && existingRealIndex !== tempIndex) {
                        // Real document already exists, just remove the temp entry
                        draft.inputs.splice(tempIndex, 1);
                      } else {
                        // Replace temp with real document
                        draft.inputs[tempIndex] = {
                          encounter_document_id: createdDocument.encounter_document_id,
                          type: createdDocument.type,
                          title: createdDocument.title || null,
                          created_at: createdDocument.created_at || new Date().toISOString(),
                          status: createdDocument.status || 'pending',
                        };
                      }
                    }
                  }
                },
              ),
            );
          } catch {
            // On error, undo the optimistic update
            patchResult.undo();
          }
        },
      },

      // Enhance: Update document
      patchV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
        // Only invalidate the document itself - encounter's inputs array (references only) doesn't change
        invalidatesTags: (_result: any, _error: any, { documentId }: any) => [
          { type: 'EncounterDocument', id: documentId },
        ],
        // Optimistic update for better UX
        onQueryStarted: async (
          { encounterId, documentId, documentUpdate }: any,
          { dispatch, queryFulfilled }: any,
        ) => {
          const patchResult = dispatch(
            encountersApi.util.updateQueryData(
              'getV2ProviderEncountersByEncounterIdDocumentsAndDocumentId',
              { encounterId, documentId },
              (draft: any) => {
                Object.assign(draft, documentUpdate);
              },
            ),
          );

          try {
            await queryFulfilled;
          } catch {
            patchResult.undo();
          }
        },
      },

      // Enhance: Delete document
      deleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentId: {
        invalidatesTags: (_result, _error, { documentId }) => [
          { type: 'EncounterDocument', id: documentId },
          // NOTE: Don't invalidate Encounter here - calling code (EncounterDocuments)
          // calls saveNow() immediately after which invalidates and re-fetches the
          // encounter. This prevents double re-fetches.
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Custom V1 endpoint: Submit encounter for processing
      submitEncounter: builder.mutation<{ note_id: string }, { encounterId: string }>({
        query: ({ encounterId }) => ({
          url: '/encounter/startEncounterProcessing',
          method: 'POST',
          body: { encounter_id: encounterId },
        }),
        // Invalidate encounter cache (status changes to 'processing')
        invalidatesTags: (_result, _error, { encounterId }) => [
          { type: 'Encounter', id: encounterId },
          { type: 'EncounterList', id: 'LIST' },
          // CRITICAL: Invalidate appointments for collision detection
          // When encounter is submitted, status changes to 'processing' which affects
          // appointment collision logic (processing encounters are treated as "has encounter")
          { type: 'NextAppointment', id: 'NEXT' },
          { type: 'AppointmentsList', id: 'LIST' },
        ],
      }),
    }),
    overrideExisting: false,
  });

/**
 * Export ONLY user-friendly hook names
 *
 * ⚠️ NEVER export verbose OpenAPI-generated names like:
 * - useGetV2ProviderEncountersByEncounterIdQuery
 *
 * ✅ ALWAYS export user-friendly names like:
 * - useGetEncounterQuery
 */
export const {
  // Query hooks
  useGetV2ProviderEncountersByEncounterIdQuery: useGetEncounterQuery,
  useGetV2ProviderEncountersQuery: useListEncountersQuery,
  useLazyGetV2ProviderEncountersByEncounterIdQuery: useLazyGetEncounterQuery,

  // Mutation hooks
  usePostV2ProviderEncountersMutation: useCreateEncounterMutation,
  usePatchV2ProviderEncountersByEncounterIdMutation: useUpdateEncounterMutation,
  useDeleteV2ProviderEncountersByEncounterIdMutation: useDeleteEncounterMutation,
  usePostV2ProviderEncountersFromAppointmentByAppointmentIdMutation:
    useCreateEncounterFromAppointmentMutation,
  usePostV2ProviderEncountersCreateTranscriptOnEncounterFromExampleMutation:
    useCreateTranscriptFromExampleMutation,
  useSubmitEncounterMutation, // Custom V1 endpoint

  // Document-related queries
  useGetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdQuery: useGetDocumentQuery,
  useLazyGetV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdQuery: useLazyGetDocumentQuery,

  // Document-related mutations
  usePostV2ProviderEncountersByEncounterIdDocumentsMutation: useCreateDocumentMutation,
  useDeleteV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdMutation:
    useDeleteDocumentMutation,
  usePatchV2ProviderEncountersByEncounterIdDocumentsAndDocumentIdMutation:
    useUpdateDocumentMutation,
} = encountersApi;
